// src/pages/notifications/NotificationsPage.tsx
// Full notifications page with list, filters, and preferences

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  Settings,
  CheckCheck,
  Trash2,
  Filter,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { useNotificationPreferences } from '../hooks/useNotifications';
import { useNotificationStore } from '../stores/notificationStore';
import { NotificationList } from '../components/NotificationList';
import { NotificationFilters } from '../components/NotificationFilters';
import { NotificationPreferences } from '../components/NotificationPreferences';
import { PushPermissionPrompt } from '../components/PushPermissionPrompt';
import { ToastContainer, showToast } from '../components/ToastNotification';
import { usePushNotifications } from '../hooks/usePushNotifications';
import type { NotificationFilters as FilterType } from '../types/notifications';

type TabType = 'notifications' | 'preferences';

export const NotificationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('notifications');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Hooks
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch,
  } = useNotifications();

  const {
    preferences,
    globalSettings,
    isLoading: preferencesLoading,
    updatePreference,
    updateGlobalSettings,
  } = useNotificationPreferences();

  const {
    isEnabled: pushEnabled,
    isSupported: pushSupported,
    subscribe: enablePush,
  } = usePushNotifications();

  const { filters, setFilters, resetFilters } = useNotificationStore();

  // Handlers
  const handleFilterChange = useCallback((newFilters: Partial<FilterType>) => {
    setFilters(newFilters);
  }, [setFilters]);

  const handleMarkAsRead = useCallback(async (id: string) => {
    try {
      await markAsRead(id);
    } catch (err) {
      showToast({
        id: `error-${Date.now()}`,
        title: 'Error',
        body: 'Failed to mark notification as read',
        category: 'system',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
      });
    }
  }, [markAsRead]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteNotification(id);
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      showToast({
        id: `error-${Date.now()}`,
        title: 'Error',
        body: 'Failed to delete notification',
        category: 'system',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
      });
    }
  }, [deleteNotification]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead();
      showToast({
        id: `success-${Date.now()}`,
        title: 'Success',
        body: 'All notifications marked as read',
        category: 'system',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      showToast({
        id: `error-${Date.now()}`,
        title: 'Error',
        body: 'Failed to mark all as read',
        category: 'system',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
      });
    }
  }, [markAllAsRead]);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
  }, [notifications, selectedIds.size]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    
    const idsToDelete = Array.from(selectedIds);
    for (const id of idsToDelete) {
      try {
        await deleteNotification(id);
      } catch (err) {
        console.error(`Failed to delete notification ${id}`, err);
      }
    }
    setSelectedIds(new Set());
  }, [selectedIds, deleteNotification]);

  const handleEnablePush = useCallback(async () => {
    try {
      await enablePush();
      showToast({
        id: `success-${Date.now()}`,
        title: 'Push Enabled',
        body: 'You will now receive push notifications',
        category: 'system',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      showToast({
        id: `error-${Date.now()}`,
        title: 'Error',
        body: 'Failed to enable push notifications',
        category: 'system',
        priority: 'normal',
        isRead: false,
        createdAt: new Date().toISOString(),
      });
    }
  }, [enablePush]);

  const handleNotificationClick = useCallback((notification: { id: string; actionUrl?: string }) => {
    if (!notifications.find(n => n.id === notification.id)?.isRead) {
      handleMarkAsRead(notification.id);
    }
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
  }, [notifications, handleMarkAsRead]);

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white border-b border-warm-gray/10 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/family"
                className="p-2 text-warm-gray hover:text-navy hover:bg-cream rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sage/10 rounded-xl flex items-center justify-center">
                  <Bell className="w-5 h-5 text-sage" />
                </div>
                <div>
                  <h1 className="text-lg font-display font-semibold text-navy">
                    Notifications
                  </h1>
                  {unreadCount > 0 && (
                    <p className="text-sm text-warm-gray">
                      {unreadCount} unread
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="p-2 text-warm-gray hover:text-navy hover:bg-cream rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 -mb-px">
            <button
              onClick={() => setActiveTab('notifications')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'notifications'
                  ? 'border-sage text-sage'
                  : 'border-transparent text-warm-gray hover:text-navy'
              }`}
            >
              All Notifications
            </button>
            <button
              onClick={() => setActiveTab('preferences')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'preferences'
                  ? 'border-sage text-sage'
                  : 'border-transparent text-warm-gray hover:text-navy'
              }`}
            >
              <Settings className="w-4 h-4" />
              Preferences
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Push Permission Prompt */}
        {pushSupported && !pushEnabled && activeTab === 'notifications' && (
          <div className="mb-6">
            <PushPermissionPrompt
              variant="banner"
              onEnable={handleEnablePush}
              onDismiss={() => {}}
              showDismiss
            />
          </div>
        )}

        {activeTab === 'notifications' ? (
          <>
            {/* Actions Bar */}
            <div className="bg-white rounded-xl border border-warm-gray/10 p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      showFilters
                        ? 'bg-sage text-white'
                        : 'bg-cream text-navy hover:bg-sage/10'
                    }`}
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                  </button>

                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-navy bg-cream hover:bg-sage/10 rounded-lg transition-colors"
                    >
                      <CheckCheck className="w-4 h-4" />
                      Mark all read
                    </button>
                  )}
                </div>

                {selectedIds.size > 0 && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-warm-gray">
                      {selectedIds.size} selected
                    </span>
                    <button
                      onClick={handleDeleteSelected}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-warm-gray/10">
                  <NotificationFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onReset={resetFilters}
                    showReset
                  />
                </div>
              )}
            </div>

            {/* Notifications List */}
            <div className="bg-white rounded-xl border border-warm-gray/10 overflow-hidden">
              <NotificationList
                notifications={notifications}
                loading={isLoading}
                error={error ? String(error) : undefined}
                onLoadMore={fetchNextPage}
                hasMore={hasNextPage}
                loadingMore={isFetchingNextPage}
                onMarkRead={handleMarkAsRead}
                onDelete={handleDelete}
                onNotificationClick={handleNotificationClick}
                groupByDate
                emptyMessage="You're all caught up! No notifications to show."
              />
            </div>
          </>
        ) : (
          /* Preferences Tab */
          <div className="bg-white rounded-xl border border-warm-gray/10 p-6">
            <NotificationPreferences
              preferences={preferences}
              globalSettings={globalSettings}
              onUpdatePreference={updatePreference}
              onUpdateGlobalSettings={updateGlobalSettings}
              loading={preferencesLoading}
            />
          </div>
        )}
      </main>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
};

export default NotificationsPage;


